import { DialogTrigger } from '@ibsys/kit';
import { Share2Icon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebouncedSearch } from '@/shared/hooks';
import { getFullName } from '@/shared/lib';
import { IconButton, SelectionDialog, useSelectionDialogState } from '@/shared/ui';
import { useSharedPresetRecipients } from '../model/api/hooks';
import type { SharedPresetUser } from '../model/api/types';

const getUserId = (user: SharedPresetUser) => user.id;
const getUserName = (user: SharedPresetUser) => getFullName(user.firstName, user.lastName, user.middleName);
const getUserEmail = (user: SharedPresetUser) => user.email;

type Props = {
  /** Заголовок окна — называет, чем делятся */
  title: string;
  /** Название действия у кнопки-значка */
  triggerLabel: string;
  /**
   * Получатели, отмеченные при открытии: у совместного пресета — его текущий состав, у личного —
   * пустой. `undefined` — состав ещё не известен
   */
  recipients: SharedPresetUser[] | undefined;
  /**
   * Поделиться с отмеченными пользователями; без отметок совместный пресет перестаёт быть
   * совместным. `onShared` закрывает окно — его вызывают по ответу сервера, отказ оставляет окно
   * открытым с прежними отметками.
   */
  onShare: (recipientIds: number[], onShared: () => void) => void;
  /** Запрос «Поделиться» в работе */
  isSharing: boolean;
};

/**
 * Кнопка «Поделиться» и окно выбора получателей совместного пресета — одно на пресеты таблицы
 * и пользовательские дашборды.
 *
 * Получателя, которого в справочнике уже нет (деактивирован после того, как с ним поделились),
 * можно снять — иначе состав с ним сервер не примет.
 *
 * Подтверждать нечего, пока отметки совпадают с текущими получателями. Снять отметки со всех
 * и подтвердить можно — так владелец перестаёт делиться; у личного пресета получателей нет,
 * и без отметок его окно не подтверждается.
 *
 * Пока текущий состав не известен, подтверждение недоступно: подтверждение заменяет состав
 * отмеченными целиком, и прежние получатели, ещё не попавшие в отметки, молча снялись бы.
 */
export const SharePresetDialog: FC<Props> = ({ title, triggerLabel, recipients, onShare, isSharing }) => {
  const { t } = useTranslation('common');

  const { isOpen, search, setSearch, selected, group, isSelectionChanged, handleOpenChange, handleToggle } =
    useSelectionDialogState<SharedPresetUser>({
      getItemId: getUserId,
      getInitialSelected: () => recipients ?? [],
    });
  const debouncedSearch = useDebouncedSearch(search);
  const { data, isLoading } = useSharedPresetRecipients(debouncedSearch, { enabled: isOpen });

  const handleConfirm = () => {
    onShare(
      selected.map((user) => user.id),
      () => handleOpenChange(false),
    );
  };

  return (
    <SelectionDialog<SharedPresetUser>
      open={isOpen}
      onOpenChange={handleOpenChange}
      trigger={
        <IconButton
          size="icon"
          className="shrink-0"
          label={triggerLabel}
          icon={<Share2Icon className="size-4" />}
          trigger={DialogTrigger}
        />
      }
      title={title}
      search={search}
      onSearchChange={setSearch}
      items={data?.items ?? []}
      total={data?.total}
      isLoading={isLoading}
      selected={selected}
      group={group}
      onToggle={handleToggle}
      getItemId={getUserId}
      renderTitle={getUserName}
      renderHint={getUserEmail}
      labels={{
        searchPlaceholder: t('sharePreset.searchPlaceholder'),
        empty: t('sharePreset.noUsers'),
        notFound: t('sharePreset.notFound'),
        confirm: t('sharePreset.confirm'),
      }}
      onConfirm={handleConfirm}
      confirmDisabled={!recipients || !isSelectionChanged}
      isConfirming={isSharing}
    />
  );
};
